import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { userService, companyService } from '../services/apiService';
import { useIsMobile } from '../hooks/useMediaQuery';
import { useAuthToken } from '../hooks/useAuthToken';
import StackedTable from '../components/StackedTable';
import ReportHeader from '../components/ReportHeader';
import { LoadingState, ErrorState } from '../components/PageState';
import { useChartTheme } from '../components/chartTheme';
import { EMPTY, formatDateTime, formatMonthYear, formatNumber, plural, roleLabel } from '../utils/format';
import { rowClick } from '../utils/rows';

const monthKey = (year, month) => `${year}-${String(month).padStart(2, '0')}`;
const longMonth = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
const shortMonth = new Intl.DateTimeFormat('en-GB', { month: 'long' });
/** "2024-03" as "March 2024", for tooltips and tile notes. */
const monthLabel = (key) => {
  const [year, month] = key.split('-').map(Number);
  return longMonth.format(new Date(year, month - 1, 1));
};

/**
 * Every month from the first one with figures to this month, users and companies side by side. The
 * backend leaves out months with no sign-ups, so they are filled in as zeros here.
 */
const buildMonths = (userRows, companyRows) => {
  const users = new Map(userRows.map((r) => [monthKey(r.year, r.month), r.count]));
  const companies = new Map(companyRows.map((r) => [monthKey(r.year, r.month), r.count]));
  const keys = [...users.keys(), ...companies.keys()].sort();
  if (!keys.length) return [];
  const [startYear, startMonth] = keys[0].split('-').map(Number);
  const cursor = new Date(startYear, startMonth - 1, 1);
  const now = new Date();
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const months = [];
  while (cursor <= end) {
    const key = monthKey(cursor.getFullYear(), cursor.getMonth() + 1);
    months.push({ month: key, userCount: users.get(key) || 0, companyCount: companies.get(key) || 0 });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

/** "Up 12% on October 2025", or the plain figure when there is nothing to compare against. */
const compareText = (current, previous, previousKey) => {
  if (previous == null) return null;
  if (previous === 0) return `${formatNumber(current)} against none in ${monthLabel(previousKey)}`;
  const change = Math.round(((current - previous) / previous) * 100);
  if (change === 0) return `Level with ${monthLabel(previousKey)} (${formatNumber(previous)})`;
  return `${change > 0 ? 'Up' : 'Down'} ${Math.abs(change)}% on ${monthLabel(previousKey)} (${formatNumber(previous)})`;
};

const SignupsTooltip = ({ active, payload, label, name, box }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={box}>
      <div className="text-muted small mb-1">{monthLabel(label)}</div>
      <strong>{formatNumber(payload[0].value)}</strong> <span className="text-muted">{name}</span>
    </div>
  );
};

const UserSignups = () => {
  const refreshToken = useAuthToken();
  const navigate = useNavigate();
  const [months, setMonths] = useState([]);
  const [recentUsers, setRecentUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usersError, setUsersError] = useState(null);
  const isMobile = useIsMobile();
  const t = useChartTheme();

  useEffect(() => {
    fetchSignupsData();
    fetchRecentUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSignupsData = async () => {
    try {
      setLoading(true);
      setError(null);
      await refreshToken();
      const [userResponse, companyResponse] = await Promise.all([
        userService.getUserSignupsByMonth(),
        companyService.getCompanySignupsByMonth(),
      ]);
      setMonths(buildMonths(userResponse.data, companyResponse.data));
    } catch (err) {
      setError('Could not load sign-ups by month.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentUsers = async () => {
    try {
      setUsersLoading(true);
      setUsersError(null);
      await refreshToken();
      const response = await userService.getRecentUsers(100);
      setRecentUsers(response.data);
    } catch (err) {
      setUsersError('Could not load recent sign-ups.');
      console.error('Failed to load recent users:', err);
    } finally {
      setUsersLoading(false);
    }
  };

  // This month, last month and the same month a year earlier (the series always ends at this month)
  const thisMonth = months[months.length - 1];
  const lastMonth = months[months.length - 2];
  const yearBefore = months[months.length - 14];

  // Both charts share the month axis: same width Y axis and margins, so the months line up
  const chartMargin = isMobile ? { top: 8, right: 8, left: 0, bottom: 0 } : { top: 8, right: 24, left: 8, bottom: 0 };
  const yAxisWidth = isMobile ? 40 : 48;
  const xAxis = {
    dataKey: 'month',
    ...t.xAxis,
    tickFormatter: (m) => formatMonthYear(m),
    interval: 'preserveStartEnd',
    minTickGap: isMobile ? 16 : 24,
  };
  const line = (dataKey) => ({
    type: 'monotone',
    dataKey,
    stroke: t.colors.emphasis,
    strokeWidth: 2,
    dot: isMobile ? false : { r: 3, fill: t.colors.emphasis },
    activeDot: { r: 5 },
  });

  const userName = (user) => [user.firstname, user.lastname].filter(Boolean).join(' ') || user.email || EMPTY;

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="User signups"
        description="Users and companies created each month over the last 3 years, and the latest 100 sign-ups."
      />

      {error ? (
        <Card className="shadow-sm mb-4"><ErrorState inCard message={error} onRetry={fetchSignupsData} /></Card>
      ) : loading ? (
        <Card className="shadow-sm mb-4"><LoadingState label="Loading sign-ups" /></Card>
      ) : months.length === 0 ? (
        <Card className="shadow-sm mb-4"><div className="text-center text-muted p-4">No sign-ups in the last 3 years</div></Card>
      ) : (
        <>
          <Row className="g-3 g-md-4 mb-4">
            <Col xs={4} md={4}>
              <Card className="shadow-sm h-100 stat-tile">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Users this month so far</h6>
                  <h2 className="text-primary mb-0">{formatNumber(thisMonth.userCount)}</h2>
                  <small className="text-muted">Since 1 {shortMonth.format(new Date())}</small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={4} md={4}>
              <Card className="shadow-sm h-100 stat-tile">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Users in {lastMonth ? monthLabel(lastMonth.month) : 'last month'}</h6>
                  <h2 className="mb-0">{lastMonth ? formatNumber(lastMonth.userCount) : EMPTY}</h2>
                  <small className="text-muted">
                    {lastMonth && yearBefore
                      ? compareText(lastMonth.userCount, yearBefore.userCount, yearBefore.month)
                      : 'No figures for a year earlier'}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={4} md={4}>
              <Card className="shadow-sm h-100 stat-tile">
                <Card.Body className="text-center">
                  <h6 className="text-muted mb-2">Companies this month so far</h6>
                  <h2 className="mb-0">{formatNumber(thisMonth.companyCount)}</h2>
                  {lastMonth && (
                    <small className="text-muted">{plural(lastMonth.companyCount, 'company', 'companies')} in {monthLabel(lastMonth.month)}</small>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-light">
              <h5 className="mb-0">Sign-ups by month</h5>
              <small className="text-muted">The latest month is still filling in.</small>
            </Card.Header>
            <Card.Body>
              {/* Two charts on one month axis, so each count has its own scale and the lines never cross by accident */}
              <h6 className="text-muted mb-2">Users</h6>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 280}>
                <LineChart data={months} margin={chartMargin} syncId="signups">
                  <CartesianGrid {...t.grid} />
                  <XAxis {...xAxis} hide />
                  <YAxis {...t.yAxis} width={yAxisWidth} allowDecimals={false} />
                  <Tooltip {...t.tooltip} content={<SignupsTooltip name="users" box={t.tooltip.contentStyle} />} />
                  <Line {...line('userCount')} name="Users" />
                </LineChart>
              </ResponsiveContainer>
              <h6 className="text-muted mt-3 mb-2">Companies</h6>
              <ResponsiveContainer width="100%" height={isMobile ? 170 : 220}>
                <LineChart data={months} margin={chartMargin} syncId="signups">
                  <CartesianGrid {...t.grid} />
                  <XAxis {...xAxis} />
                  <YAxis {...t.yAxis} width={yAxisWidth} allowDecimals={false} />
                  <Tooltip {...t.tooltip} content={<SignupsTooltip name="companies" box={t.tooltip.contentStyle} />} />
                  <Line {...line('companyCount')} name="Companies" />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </>
      )}

      <Card className="shadow-sm">
        <Card.Header className="bg-light">
          <h5 className="mb-0">Latest 100 sign-ups</h5>
        </Card.Header>
        <Card.Body className="p-0">
          {usersError ? (
            <ErrorState inCard message={usersError} onRetry={fetchRecentUsers} />
          ) : usersLoading ? (
            <LoadingState label="Loading recent sign-ups" />
          ) : recentUsers.length === 0 ? (
            <div className="text-center text-muted p-4">
              No recent sign-ups
            </div>
          ) : (
            <StackedTable responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="d-none d-xl-table-cell">Role</th>
                  <th>Company</th>
                  <th>Signed up</th>
                </tr>
              </thead>
              <tbody>
                {recentUsers.map((user) => (
                  <tr key={user.id} onClick={rowClick(() => navigate(`/users/${user.id}`))} style={{ cursor: 'pointer' }}>
                    <td>
                      <Link className="cell-link" to={`/users/${user.id}`}>{userName(user)}</Link>
                    </td>
                    <td>{user.email || EMPTY}</td>
                    <td className="d-none d-xl-table-cell">{roleLabel(user.role)}</td>
                    <td>
                      {user.companyId && user.companyName
                        ? <Link to={`/companies/${user.companyId}`}>{user.companyName}</Link>
                        : user.companyName || EMPTY}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(user.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </StackedTable>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default UserSignups;
